<script>
import api, { assetUrl, portalUrl } from "@/helpers/api";

// Map a legacy admin Link string (e.g. "DEFAULT", "OUR-EXPERTS?MENU=OUREXPERTS",
// "S-CONTACT_US?MENU=CONTACT US", "SUBMENU-CONTENT?SUBMENU=ABOUT") to a Sunhera
// Vue route. Returns:
//   { kind: "route", path: "/foo" }       -> use <router-link>
//   { kind: "external", href: "https..." } -> use plain <a href>
//   null                                   -> not a navigable link (parent of dropdown)
function mapLegacyLink(rawLink) {
  if (!rawLink || !rawLink.trim()) return null;
  const link = rawLink.trim().toUpperCase();

  // Common legacy → Vue route mappings (prefix match before suffix params).
  const prefixMap = [
    { prefix: "DEFAULT", path: "/" },
    { prefix: "HOME", path: "/" },
    { prefix: "OUR-EXPERTS", path: "/experts" },
    { prefix: "BLOG-DETAILS", path: "/blog" },
    { prefix: "BLOG", path: "/blog" },
    { prefix: "S-CONTACT_US", path: "/contact" },
    { prefix: "CONTACT-US", path: "/contact" },
    { prefix: "CONTACT_US", path: "/contact" },
    { prefix: "FIND-JOBS", path: "/find-jobs" },
    { prefix: "FIND JOBS", path: "/find-jobs" },
    { prefix: "FIND JOB", path: "/find-jobs" },
    { prefix: "FINDJOBS", path: "/find-jobs" },
    { prefix: "FINDS-JOB", path: "/find-jobs" },
    { prefix: "PROFILE-HIGHLIGHTS", path: "/profile-highlights" },
    { prefix: "PROFILE_HIGHLIGHTS", path: "/profile-highlights" },
    { prefix: "PROFILEHIGHLIGHT", path: "/profile-highlights" },
    { prefix: "PROFILE-HIGHLIGHT", path: "/profile-highlights" },
    { prefix: "PROFILE HIGHLIGHT", path: "/profile-highlights" },
    { prefix: "BUILD-YOUR-RESUME", path: "/build-resume" },
    { prefix: "BUILD YOUR RESUME", path: "/build-resume" },
    { prefix: "BUILDYOURRESUME", path: "/build-resume" },
    { prefix: "BUILD-RESUME", path: "/build-resume" },
    { prefix: "ABOUT-US", path: "/about" },
    { prefix: "ABOUT", path: "/about" },
    { prefix: "GALLERY", path: "/about/gallery" },
    { prefix: "PHOTO-GALLERY", path: "/about/gallery" },
    { prefix: "REFUND-POLICY", path: "/policies/refund" },
    { prefix: "REFUND_POLICY", path: "/policies/refund" },
    { prefix: "REFUND POLICY", path: "/policies/refund" },
    { prefix: "REFUND", path: "/policies/refund" },
    { prefix: "FAQ", path: "/faq/candidate" },
  ];
  for (const { prefix, path } of prefixMap) {
    if (link.startsWith(prefix)) return { kind: "route", path };
  }

  // Known keywords mapped to richer dedicated pages. Used by both the
  // SUBMENU-CONTENT?SUBMENU=X and the more permissive ?MENU=X / ?SUBMENU=X
  // patterns admins sometimes save.
  const keyMap = {
    ABOUT: "/about",
    "ABOUT US": "/about",
    DIRECTOR: "/about/founder",
    MENTOR: "/about/mentor",
    INSPIRATIONS: "/about/inspiration",
    WHYUS: "/about/why-us",
    "WHY-US": "/about/why-us",
    "WHY US": "/about/why-us",
    PHOTO: "/about/gallery",
    "PHOTO GALLERY": "/about/gallery",
    GALLERY: "/about/gallery",
    FAQ: "/faq/candidate",
    FAQ1: "/faq/employer",
    REFUND: "/policies/refund",
  };

  // Legacy SUBMENU-CONTENT?SUBMENU=KEYWORD — route by keyword. Unknown keywords
  // fall through to the generic /content/:keyword route, which renders the
  // sub-menu's ContentHtml from master data — so admins can add new pages
  // by just creating a sub-menu with a fresh KEYWORD, no code changes.
  const submenuMatch = /SUBMENU-CONTENT\?SUBMENU\s*=\s*(.+)/.exec(link);
  if (submenuMatch) {
    const key = submenuMatch[1].trim();
    if (keyMap[key]) return { kind: "route", path: keyMap[key] };
    return { kind: "route", path: `/content/${encodeURIComponent(key)}` };
  }

  // Permissive fallback for admin-saved URLs that just have ?MENU= or
  // ?SUBMENU= without the SUBMENU-CONTENT prefix (e.g. "GALLERY?MENU=PHOTO GALLERY").
  // Try the keyword first, then the path part before the "?".
  const paramMatch = /[?&](?:SUB)?MENU\s*=\s*([^&]+)/.exec(link);
  if (paramMatch) {
    const key = paramMatch[1].trim();
    if (keyMap[key]) return { kind: "route", path: keyMap[key] };
    // Fall through to generic content route if no known keyword match.
    return { kind: "route", path: `/content/${encodeURIComponent(key)}` };
  }

  // Absolute external URL.
  if (/^https?:\/\//i.test(rawLink)) return { kind: "external", href: rawLink };

  // Already a Vue path.
  if (rawLink.startsWith("/")) return { kind: "route", path: rawLink };

  // Last resort — treat as relative external.
  return { kind: "external", href: rawLink };
}

// Legacy About Us dropdown has 6 fixed sub-menus. Tenants with a partially-seeded
// JcSubMenu table may be missing some (Photo Gallery in particular). Append any
// missing ones so the dropdown never regresses vs the legacy Sunhera site.
const ABOUT_LEGACY_SUBMENUS = [
  { title: "About Us", url: "SUBMENU-CONTENT?SUBMENU=ABOUT" },
  { title: "Our Inspirations", url: "SUBMENU-CONTENT?SUBMENU=INSPIRATIONS" },
  { title: "Our Mentor", url: "SUBMENU-CONTENT?SUBMENU=MENTOR" },
  { title: "Our Founder", url: "SUBMENU-CONTENT?SUBMENU=DIRECTOR" },
  { title: "Photo Gallery", url: "SUBMENU-CONTENT?SUBMENU=PHOTO" },
  { title: "Why Us", url: "SUBMENU-CONTENT?SUBMENU=WHYUS" },
];

function ensureAboutSubMenus(menus) {
  if (!Array.isArray(menus)) return menus;
  let synthId = -1000;
  for (const m of menus) {
    const t = (m?.title || "").trim().toUpperCase();
    if (t !== "ABOUT US" && t !== "ABOUT") continue;
    const existing = Array.isArray(m.subMenus) ? m.subMenus.slice() : [];
    const norm = (s) => (s || "").trim().toUpperCase().replace(/[\s_-]+/g, " ");
    const have = new Set(existing.map((s) => norm(s.title)));
    for (const legacy of ABOUT_LEGACY_SUBMENUS) {
      if (!have.has(norm(legacy.title))) {
        existing.push({ id: synthId--, title: legacy.title, url: legacy.url, subMenus: [] });
      }
    }
    m.subMenus = existing;
  }
  return menus;
}

// Special-action sub-menus that route to the portal instead of a public route.
// Triggered by sub-menu TITLE (admin can name them as legacy did).
// Returns null, or { kind, redirect? } where redirect is an optional portal path
// the portal login page should send the user to after auth.
//
// Note: BUILD YOUR RESUME / RESUME used to live here (login bounce), but the
// public site now has a dedicated /build-resume page (Tips + EXAMPLE card +
// CTA -> portal login). The CTA on that page handles the deep-link directly.
function specialAction(title) {
  const t = (title || "").trim().toUpperCase();
  if (t === "HIRE NOW") return { kind: "employerRegister" };
  return null;
}

export default {
  name: "SiteHeader",
  data() {
    return {
      menus: [],
      settings: {},
      mobileOpen: false,
      logoImgFailed: false,
    };
  },
  computed: {
    // Legacy parity: static /images/logo.png first (matches jobieweb/images/logo.png),
    // fall back to admin-uploaded LogoLightUrl only if the static file 404s.
    logoUrl() {
      if (!this.logoImgFailed) return `${process.env.BASE_URL || "/"}images/logo.png`;
      if (this.settings.logoLightUrl) return assetUrl(this.settings.logoLightUrl);
      return null;
    },
    title() { return this.settings.siteTitle || "Sunhera Job"; },
  },
  async mounted() {
    await Promise.all([this.loadMenus(), this.loadSettings()]);
  },
  methods: {
    async loadMenus() {
      try {
        const { data } = await api.get("/public/jc/menus");
        this.menus = ensureAboutSubMenus(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn("Menu load failed", err);
      }
    },
    async loadSettings() {
      try {
        const { data } = await api.get("/public/jc/site-settings");
        this.settings = data || {};
      } catch (err) {
        console.warn("Settings load failed", err);
      }
    },
    portalLoginUrl(role, redirect) {
      const base = `${portalUrl()}/${role}/login`;
      return redirect ? `${base}?redirect=${encodeURIComponent(redirect)}` : base;
    },
    seekerLogin(redirect) { window.location.href = this.portalLoginUrl("seeker", redirect); },
    employerLogin(redirect) { window.location.href = this.portalLoginUrl("employer", redirect); },
    employerRegister() { window.location.href = `${portalUrl()}/employer/register`; },

    mapLink(menu) {
      // Legacy admin often leaves LINK blank on well-known sub-menus
      // (FIND JOBS, BUILD YOUR RESUME, PROFILE HIGHLIGHT) and the PHP
      // frontend resolves them by TITLE. Mirror that fallback here.
      return mapLegacyLink(menu.url) || mapLegacyLink(menu.title);
    },

    onSpecialClick(action, ev) {
      ev.preventDefault();
      if (!action) return;
      if (action.kind === "seekerLogin") this.seekerLogin(action.redirect);
      else if (action.kind === "employerLogin") this.employerLogin(action.redirect);
      else if (action.kind === "employerRegister") this.employerRegister();
    },

    hasSubMenus(menu) {
      return Array.isArray(menu.subMenus) && menu.subMenus.length > 0;
    },

    actionFor(item) {
      return specialAction(item.title);
    },
  },
};
</script>

<template>
  <header class="site-header">
    <nav class="navbar navbar-expand-lg container">
      <router-link to="/" class="navbar-brand sunhera-brand">
        <img v-if="logoUrl" :src="logoUrl" :alt="title" @error="logoImgFailed = true" />
        <template v-else>
          <span class="brand-icon">
            <i class="ri-search-eye-line"></i>
          </span>
          <span class="brand-text">
            <span class="brand-name">{{ title }}</span>
            <span class="brand-tagline">{{ settings.tagline || "UNLOCK YOUR FUTURE" }}</span>
          </span>
        </template>
      </router-link>

      <button class="navbar-toggler" type="button" @click="mobileOpen = !mobileOpen">
        <span class="navbar-toggler-icon"></span>
      </button>

      <div class="collapse navbar-collapse" :class="{ show: mobileOpen }">
        <ul class="navbar-nav mx-auto">
          <li
            v-for="menu in menus"
            :key="menu.id"
            class="nav-item"
            :class="{ dropdown: hasSubMenus(menu) }"
          >
            <!-- Parent with sub-menus → dropdown -->
            <template v-if="hasSubMenus(menu)">
              <a
                class="nav-link dropdown-toggle"
                href="#"
                data-bs-toggle="dropdown"
                role="button"
                aria-expanded="false"
              >{{ menu.title }}</a>
              <ul class="dropdown-menu">
                <li v-for="sub in menu.subMenus" :key="sub.id">
                  <!-- Sub-menu: special action takes priority over link -->
                  <a
                    v-if="actionFor(sub)"
                    href="#"
                    class="dropdown-item"
                    @click="onSpecialClick(actionFor(sub), $event)"
                  >{{ sub.title }}</a>
                  <router-link
                    v-else-if="mapLink(sub) && mapLink(sub).kind === 'route'"
                    :to="mapLink(sub).path"
                    class="dropdown-item"
                  >{{ sub.title }}</router-link>
                  <a
                    v-else-if="mapLink(sub) && mapLink(sub).kind === 'external'"
                    :href="mapLink(sub).href"
                    class="dropdown-item"
                    target="_blank"
                    rel="noopener"
                  >{{ sub.title }}</a>
                  <span v-else class="dropdown-item-text">{{ sub.title }}</span>
                </li>
              </ul>
            </template>

            <!-- Leaf menu → direct link -->
            <template v-else>
              <a
                v-if="actionFor(menu)"
                href="#"
                class="nav-link"
                @click="onSpecialClick(actionFor(menu), $event)"
              >{{ menu.title }}</a>
              <router-link
                v-else-if="mapLink(menu) && mapLink(menu).kind === 'route'"
                :to="mapLink(menu).path"
                class="nav-link"
                exact-active-class="router-link-active"
              >{{ menu.title }}</router-link>
              <a
                v-else-if="mapLink(menu) && mapLink(menu).kind === 'external'"
                :href="mapLink(menu).href"
                class="nav-link"
                target="_blank"
                rel="noopener"
              >{{ menu.title }}</a>
              <span v-else class="nav-link disabled">{{ menu.title }}</span>
            </template>
          </li>
        </ul>

        <div class="d-flex gap-2">
          <router-link to="/login" class="btn signup-login-btn">SignUp/Login</router-link>
        </div>
      </div>
    </nav>
  </header>
</template>
